import { api } from './client'
import { fetchAllProducts, fetchOperators } from './catalog'
import { campaignsApi } from './campaigns'
import { contestsApi } from './performance'
import { usersApi } from './users'

/**
 * F-064 / TASK-0318 — controllo dei costi (REQ-148-001 centri di costo, REQ-146-001 spese ricorrenti,
 * REQ-151-001/REQ-152-001 budget di costo, REQ-169-001/REQ-176-001/REQ-177-003 dashboard e
 * scadenziario, REQ-187-001 piano gestionale). Tutti i calcoli (consuntivo, scostamento, alert,
 * somme per categoria) arrivano dal server: qui si mostrano soltanto. Solo costi: nessun ricavo,
 * margine o ROI. Gli endpoint di scrittura e la dashboard sono Admin; i centri di costo si leggono
 * da qualunque utente interno (servono al menu della spesa).
 */

export interface CostCentre {
  id: number
  code: string
  name: string
  departmentId?: number | null
  isActive: boolean
  /** R3-10 (flag finanza.dimensioni_estese): dimensioni e organico; `null` = non assegnati. */
  businessUnitId?: number | null
  siteId?: number | null
  teamId?: number | null
  headcount?: number | null
}

/** R3-10: campi nuovi di centro di costo; omesso = non toccare, 0 = togliere. */
export interface CostCentreExtendedBody {
  businessUnitId?: number | undefined
  siteId?: number | undefined
  teamId?: number | undefined
  headcount?: number | undefined
}

export const costCentresApi = {
  list: (includeInactive?: boolean) =>
    api<CostCentre[]>(`/api/finance/cost-centres${includeInactive ? '?includeInactive=true' : ''}`),
  create: (body: { code: string; name: string; departmentId?: number | undefined } & CostCentreExtendedBody) =>
    api<{ id: number }>('/api/finance/cost-centres', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: { name?: string | undefined; departmentId?: number | undefined; clearDepartment?: boolean | undefined; isActive?: boolean | undefined } & CostCentreExtendedBody) =>
    api<void>(`/api/finance/cost-centres/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
}

export const RECURRING_FREQUENCIES = ['Mensile', 'Bimestrale', 'Trimestrale', 'Semestrale', 'Annuale', 'Personalizzata'] as const

export interface RecurringExpense {
  id: number
  description: string
  expenseCategoryId: number
  costCentreId?: number | null
  supplierId?: number | null
  netAmount: number
  vatAmount: number
  frequency: string
  intervalMonths?: number | null
  dayOfMonth: number
  startDate: string
  endDate?: string | null
  isActive: boolean
}

export interface RecurringExpenseBody {
  description: string
  expenseCategoryId: number
  costCentreId?: number | undefined
  supplierId?: number | undefined
  netAmount: number
  vatAmount: number
  frequency: string
  intervalMonths?: number | undefined
  dayOfMonth: number
  startDate: string
  endDate?: string | undefined
  isActive?: boolean | undefined
}

export const recurringExpensesApi = {
  list: (includeInactive?: boolean) =>
    api<RecurringExpense[]>(`/api/finance/recurring-expenses${includeInactive ? '?includeInactive=true' : ''}`),
  create: (body: RecurringExpenseBody) =>
    api<{ id: number }>('/api/finance/recurring-expenses', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: RecurringExpenseBody) =>
    api<void>(`/api/finance/recurring-expenses/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deactivate: (id: number) => api<void>(`/api/finance/recurring-expenses/${id}`, { method: 'DELETE' }),
  /** Genera subito le scadenze mancanti dalla data di inizio fino a `until` (idempotente). */
  generate: (id: number, until?: string) =>
    api<{ created: number; until: string }>(
      `/api/finance/recurring-expenses/${id}/generate${until ? `?until=${until}` : ''}`, { method: 'POST' }),
}

export const BUDGET_PERIOD_KINDS = ['Annuale', 'Trimestrale', 'Mensile'] as const
export const BUDGET_DIMENSIONS = ['Totale', 'CentroDiCosto', 'Categoria', 'Settore'] as const
/** R3-10 (flag finanza.dimensioni_estese): budget per team (id = responsabile della squadra) e per campagna. */
export const BUDGET_DIMENSIONS_EXTENDED = [...BUDGET_DIMENSIONS, 'Team', 'Campagna'] as const

export interface BudgetBody {
  year: number
  periodKind: string
  periodIndex?: number | undefined
  dimension: string
  dimensionId?: number | undefined
  amount: number
  notes?: string | undefined
}

export interface BudgetVariance {
  id: number
  year: number
  periodKind: string
  periodIndex?: number | null
  dimension: string
  dimensionId?: number | null
  notes?: string | null
  from: string
  to: string
  budget: number
  actual: number
  actualNet: number
  variance: number
  /** 0,175 = +17,5%. `null` se il budget è 0. */
  variancePct?: number | null
  alert: boolean
}

export const budgetsApi = {
  create: (body: BudgetBody) =>
    api<{ id: number }>('/api/finance/budgets', { method: 'POST', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`/api/finance/budgets/${id}`, { method: 'DELETE' }),
  variance: (year: number) =>
    api<{ year: number; threshold: number; items: BudgetVariance[] }>(`/api/finance/budgets/variance?year=${year}`),
}

export interface CostCategoryNode {
  id: number
  name: string
  parentId?: number | null
  ownTotal: number
  total: number
  children: CostCategoryNode[]
}

export interface CostsDashboard {
  from: string
  to: string
  totals: { total: number; net: number; recurring: number; nonRecurring: number; count: number }
  month: { from: string; to: string; total: number }
  year: { from: string; to: string; total: number }
  previousPeriod: { from: string; to: string; total: number; delta: number; deltaPct?: number | null }
  byCategory: CostCategoryNode[]
  bySector: { departmentId?: number | null; total: number }[]
  byCostCentre: { costCentreId?: number | null; code?: string | null; name?: string | null; total: number }[]
  threshold: number
  budgets: BudgetVariance[]
  alerts: BudgetVariance[]
}

export interface ScheduleItem {
  id: number
  description: string
  dueDate: string
  totalAmount: number
  status: string
  expenseCategoryId: number
  costCentreId?: number | null
  supplierId?: number | null
  recurring: boolean
}

export interface CostSchedule {
  today: string
  days: number
  until: string
  total: number
  overdueTotal: number
  items: ScheduleItem[]
  overdue: ScheduleItem[]
}

/**
 * F-092 / REQ-149-001 — dimensioni analitiche della spesa. Sono id di altri moduli (nessuna FK sul server):
 * operatore e prodotto (Catalog), campagna (Leads), gara (Settings), agente (utente); il progetto è un codice libero.
 * Il server restituisce gli id; i nomi si risolvono qui con le liste esistenti.
 */
export const COST_DIMENSIONS = [
  { id: 'operator', label: 'Operatore' },
  { id: 'product', label: 'Prodotto' },
  { id: 'campaign', label: 'Campagna' },
  { id: 'contest', label: 'Gara' },
  { id: 'agent', label: 'Agente' },
  { id: 'project', label: 'Progetto' },
] as const

export type CostDimensionId = (typeof COST_DIMENSIONS)[number]['id']

export interface DimensionOption {
  id: number
  label: string
}

/** Le liste per le cinque dimensioni con id (il progetto è un codice libero e non ha lista). */
export type DimensionOptions = Record<Exclude<CostDimensionId, 'project'>, DimensionOption[]>

export const EMPTY_DIMENSION_OPTIONS: DimensionOptions = {
  operator: [], product: [], campaign: [], contest: [], agent: [],
}

/**
 * Carica le liste per i selettori. Ogni lista è indipendente e senza errori: se una non è leggibile dal ruolo
 * (gli utenti li vede solo l'Admin) resta vuota e la pagina ripiega sull'inserimento dell'id.
 */
export async function loadDimensionOptions(): Promise<DimensionOptions> {
  const [operator, product, campaign, contest, agent] = await Promise.all([
    fetchOperators().then(r => r.items.map(o => ({ id: o.id, label: o.name }))).catch(() => []),
    fetchAllProducts().then(r => r.items.map(p => ({ id: p.id, label: p.name }))).catch(() => []),
    campaignsApi.list({ pageSize: 200 }).then(r => r.items.map(c => ({ id: c.id, label: c.name }))).catch(() => []),
    contestsApi.list().then(r => r.items.map(c => ({ id: c.id, label: c.name }))).catch(() => []),
    usersApi.list({ size: 200, isActive: true }).then(r => r.items.map(u => ({ id: u.id, label: u.name }))).catch(() => []),
  ])
  return { operator, product, campaign, contest, agent }
}

export interface CostByDimensionItem {
  /** L'id (come testo) o il codice progetto; `null` = "Non assegnato". */
  key: string | null
  label: string
  assigned: boolean
  total: number
  net: number
  count: number
}

export interface CostByDimension {
  dimension: CostDimensionId
  from: string
  to: string
  totals: { total: number; net: number; count: number }
  items: CostByDimensionItem[]
}

export const costsApi = {
  /** F-092: costi del periodo per dimensione. Admin e ruoli di sola lettura amministrativa (Commercialista). */
  byDimension: (dimension: CostDimensionId, from?: string, to?: string) => {
    const q = new URLSearchParams({ dimension })
    if (from) q.set('from', from)
    if (to) q.set('to', to)
    return api<CostByDimension>(`/api/finance/costs/by-dimension?${q}`)
  },
  dashboard: (from?: string, to?: string) => {
    const q = new URLSearchParams()
    if (from) q.set('from', from)
    if (to) q.set('to', to)
    const qs = q.toString()
    return api<CostsDashboard>(`/api/finance/costs${qs ? `?${qs}` : ''}`)
  },
  schedule: (days: 7 | 30 | 60 | 90) => api<CostSchedule>(`/api/finance/costs/schedule?days=${days}`),
}

/** Le categorie come le restituisce `GET /api/expense-categories`, con il padre del piano gestionale. */
export interface CostCategory {
  id: number
  name: string
  isActive: boolean
  parentId?: number | null
  /** F-085: gruppo dello scadenziario scelto per la categoria (null = nessuno). */
  calendarBucket?: string | null
}

export const costCategoriesApi = {
  list: (includeInactive?: boolean) =>
    api<CostCategory[]>(`/api/expense-categories${includeInactive ? '?includeInactive=true' : ''}`),
}

/** Sposta una categoria di spesa sotto un padre (`null` = radice). Admin; 400 su ciclo o oltre 3 livelli. */
export const categoryHierarchyApi = {
  setParent: (id: number, parentId: number | null) =>
    api<void>(`/api/expense-categories/${id}/parent`, { method: 'PATCH', body: JSON.stringify({ parentId }) }),
}

/* ── F-121 — ripartizione dei costi comuni (REQ-150-001, REQ-251-001, REQ-149-001) ─────────────── */

/**
 * Regole versionate di ripartizione dei costi comuni fra centri di costo. Bozza (modificabile) → Attiva (immutabile)
 * → Ritirata (definitiva). Tutti i calcoli arrivano dal server e riguardano il NETTO (IVA esclusa). "Per fatturato" e
 * pro rata sui ricavi non esistono. I "dipendenti" sono pesi inseriti a mano, non un'anagrafica.
 */
export const ALLOCATION_METHODS = [
  { id: 'manuale', label: 'Manuale (percentuali)' },
  { id: 'pesi', label: 'Pesi (es. dipendenti)' },
  { id: 'per_dipendenti', label: 'Per dipendenti (organico dei centri)' },
  { id: 'produzione_inserite', label: 'Produzione: pratiche inserite' },
  { id: 'produzione_attivate', label: 'Produzione: pratiche attivate' },
] as const

export type AllocationMethod = (typeof ALLOCATION_METHODS)[number]['id']

export interface AllocationTarget {
  targetCostCentreId: number
  percentage?: number | null
  weight?: number | null
}

export interface AllocationRule {
  id: number
  name: string
  method: AllocationMethod
  status: 'Bozza' | 'Attiva' | 'Ritirata'
  sourceCostCentreId: number
  expenseCategoryId?: number | null
  validFrom: string
  validTo?: string | null
  changeReason?: string | null
  targets: AllocationTarget[]
}

export interface AllocationRuleBody {
  name: string
  method: AllocationMethod
  sourceCostCentreId: number
  expenseCategoryId?: number | undefined
  validFrom: string
  validTo?: string | undefined
  changeReason?: string | undefined
  targets: AllocationTarget[]
}

export interface AllocationPreview {
  ruleId: number
  method: AllocationMethod
  year: number
  month: number
  /** false = i conteggi di produzione non sono disponibili. */
  available: boolean
  totalQuota: number
  quotas: { targetCostCentreId: number; quota: number; sharePct: number }[]
}

export interface AllocationReport {
  from: string
  to: string
  basis: 'netto'
  productionAvailable: boolean
  costCentres: {
    costCentreId: number
    code: string
    name: string
    direct: number
    received: number
    ceded: number
    total: number
  }[]
  rules: {
    ruleId: number
    name: string
    method: AllocationMethod
    status: string
    sourceCostCentreId: number
    months: {
      year: number
      month: number
      expensesCount: number
      quotas: { targetCostCentreId: number; quota: number; amount: number }[]
    }[]
  }[]
  unallocated: {
    expenseId: number
    ruleId: number
    costCentreId: number
    expenseDate: string
    netAmount: number
    reason: 'NESSUNA_PRODUZIONE' | 'PRODUZIONE_NON_DISPONIBILE' | string
  }[]
}

export const costAllocationApi = {
  list: (status?: string) =>
    api<AllocationRule[]>(`/api/finance/cost-allocation-rules${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  create: (body: AllocationRuleBody) =>
    api<{ id: number }>('/api/finance/cost-allocation-rules', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: AllocationRuleBody) =>
    api<void>(`/api/finance/cost-allocation-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  /** 409 se un'altra regola attiva sulla stessa fonte ha date sovrapposte. */
  activate: (id: number) => api<void>(`/api/finance/cost-allocation-rules/${id}/activate`, { method: 'POST' }),
  retire: (id: number) => api<void>(`/api/finance/cost-allocation-rules/${id}/retire`, { method: 'POST' }),
  preview: (id: number, month?: string) =>
    api<AllocationPreview>(`/api/finance/cost-allocation-rules/${id}/preview${month ? `?month=${month}` : ''}`),
  report: (from?: string, to?: string) => {
    const qs = new URLSearchParams()
    if (from) qs.set('from', from)
    if (to) qs.set('to', to)
    const s = qs.toString()
    return api<AllocationReport>(`/api/finance/costs/allocation${s ? `?${s}` : ''}`)
  },
}

/* ── F-125: costi variabili generati dalla produzione ───────────────────────────────────────── */

export type VariableCostBasis = 'inserite' | 'attivate'

export const VARIABLE_COST_BASES: { id: VariableCostBasis; label: string }[] = [
  { id: 'inserite', label: 'Pratiche inserite' },
  { id: 'attivate', label: 'Pratiche attivate' },
]

export interface VariableCostRule {
  id: number
  name: string
  basis: VariableCostBasis
  /** null = tutti i settori. */
  departmentId?: number | null
  unitNetAmount: number
  unitVatAmount: number
  expenseCategoryId: number
  costCentreId?: number | null
  supplierId?: number | null
  status: 'Bozza' | 'Attiva' | 'Ritirata'
  validFrom: string
  validTo?: string | null
  changeReason?: string | null
}

export interface VariableCostRuleBody {
  name: string
  basis: VariableCostBasis
  departmentId?: number | undefined
  unitNetAmount: number
  unitVatAmount: number
  expenseCategoryId: number
  costCentreId?: number | undefined
  supplierId?: number | undefined
  validFrom: string
  validTo?: string | undefined
  changeReason?: string | undefined
}

export type VariableCostSkipReason = 'QUANTITA_ZERO' | 'MESE_CHIUSO' | 'GIA_GENERATA' | 'PRODUZIONE_NON_DISPONIBILE'

export interface VariableCostMonth {
  ruleId: number
  month: string
  complete: boolean
  withinValidity: boolean
  productionAvailable: boolean
  quantity?: number | null
  netAmount: number
  vatAmount: number
  skipReason?: VariableCostSkipReason | null
  wouldGenerate: boolean
}

export interface VariableCostGenerateResult {
  created: boolean
  expenseId?: number | null
  month: VariableCostMonth
}

export interface VariableCostReport {
  from: string
  to: string
  productionAvailable: boolean
  rows: {
    ruleId: number
    ruleName: string
    basis: VariableCostBasis
    departmentId?: number | null
    month: string
    currentQuantity?: number | null
    generatedQuantity?: number | null
    generatedNetAmount?: number | null
    generatedVatAmount?: number | null
    expenseId?: number | null
    expenseStatus?: string | null
    difference?: number | null
    skipReason?: VariableCostSkipReason | null
  }[]
}

export const variableCostApi = {
  list: (status?: string) =>
    api<VariableCostRule[]>(`/api/finance/variable-cost-rules${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  create: (body: VariableCostRuleBody) =>
    api<{ id: number }>('/api/finance/variable-cost-rules', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: VariableCostRuleBody) =>
    api<void>(`/api/finance/variable-cost-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  /** 409 se un'altra regola attiva ha la stessa chiave su mesi sovrapposti. */
  activate: (id: number) => api<void>(`/api/finance/variable-cost-rules/${id}/activate`, { method: 'POST' }),
  retire: (id: number) => api<void>(`/api/finance/variable-cost-rules/${id}/retire`, { method: 'POST' }),
  preview: (id: number, month?: string) =>
    api<VariableCostMonth>(`/api/finance/variable-cost-rules/${id}/preview${month ? `?month=${month}` : ''}`),
  /** Un mese completo e passato; idempotente. */
  generate: (id: number, month: string) =>
    api<VariableCostGenerateResult>(`/api/finance/variable-cost-rules/${id}/generate?month=${month}`, { method: 'POST' }),
  report: (from?: string, to?: string) => {
    const qs = new URLSearchParams()
    if (from) qs.set('from', from)
    if (to) qs.set('to', to)
    const s = qs.toString()
    return api<VariableCostReport>(`/api/finance/variable-costs${s ? `?${s}` : ''}`)
  },
}

/* ── R3-10: dimensioni estese (flag finanza.dimensioni_estese) ───────────────────────────────── */

export interface DimensionOptionItem {
  id: number
  name: string
  isActive: boolean
}

export interface DimensionOptionGroup {
  /** `false` quando la lista non ha voci attive: la dimensione non si mostra. */
  visible: boolean
  items: DimensionOptionItem[]
}

export interface DimensionOptionsResponse {
  enabled: boolean
  businessUnits: DimensionOptionGroup
  sites: DimensionOptionGroup
}

export const EMPTY_DIMENSION_OPTIONS_RESPONSE: DimensionOptionsResponse = {
  enabled: false,
  businessUnits: { visible: false, items: [] },
  sites: { visible: false, items: [] },
}

export const dimensionOptionsApi = {
  list: (includeInactive?: boolean) =>
    api<DimensionOptionsResponse>(`/api/finance/dimension-options${includeInactive ? '?includeInactive=true' : ''}`),
  create: (kind: 'bu' | 'sede', name: string) =>
    api<{ id: number }>('/api/finance/dimension-options', { method: 'POST', body: JSON.stringify({ kind, name }) }),
  update: (id: number, body: { name?: string | undefined; isActive?: boolean | undefined }) =>
    api<void>(`/api/finance/dimension-options/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
}

export interface IncomeCategory {
  id: number
  name: string
  parentId?: number | null
  isActive: boolean
  externalAccountCode?: string | null
}

export const incomeCategoriesApi = {
  list: (includeInactive?: boolean) =>
    api<IncomeCategory[]>(`/api/income-categories${includeInactive ? '?includeInactive=true' : ''}`),
  create: (body: { name: string; parentId?: number | undefined; externalAccountCode?: string | undefined }) =>
    api<{ id: number }>('/api/income-categories', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: { name?: string | undefined; isActive?: boolean | undefined; externalAccountCode?: string | undefined }) =>
    api<void>(`/api/income-categories/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  setParent: (id: number, parentId: number | null) =>
    api<void>(`/api/income-categories/${id}/parent`, { method: 'PATCH', body: JSON.stringify({ parentId }) }),
}

export interface ChartAccount {
  kind: 'spesa' | 'ricavo'
  id: number
  name: string
  parentId?: number | null
  externalAccountCode?: string | null
  isActive: boolean
}

export const chartOfAccountsApi = {
  get: (includeInactive?: boolean) =>
    api<{ expenses: ChartAccount[]; income: ChartAccount[] }>(`/api/finance/chart-of-accounts${includeInactive ? '?includeInactive=true' : ''}`),
}

/** Metodi di pagamento di una spesa (stesso elenco del server). */
export const PAYMENT_METHODS = [
  { id: 'Bonifico', label: 'Bonifico' },
  { id: 'Carta', label: 'Carta' },
  { id: 'Contanti', label: 'Contanti' },
  { id: 'Assegno', label: 'Assegno' },
  { id: 'AddebitoDiretto', label: 'Addebito diretto' },
  { id: 'Altro', label: 'Altro' },
] as const
